import React, { useState, useEffect, useRef } from 'react';
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import axios from 'axios';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';

const TicketManager = () => {
  const [datosCargados, setDatosCargados] = useState(false);
  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);
  const [todosLosTickets, setTodosLosTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage] = useState(20);
  const [searchText, setSearchText] = useState('');

  // Filtros del menú lateral
  const [selectedEstadoTicket, setSelectedEstadoTicket] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedEstadoChat, setSelectedEstadoChat] = useState('');
  const [selectedPrioridad, setSelectedPrioridad] = useState('');
  const [selectedUsuarioEncargado, setSelectedUsuarioEncargado] = useState('');

  const [tecnicos, setTecnicos] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};

  // Ref para el menú lateral
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mostrarFiltros && menuRef.current && !menuRef.current.contains(event.target)) {
        setMostrarFiltros(false);
      }
    };

    if (mostrarFiltros) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarFiltros]);

  // Loader mínimo de 1 segundo
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimoTiempoCumplido(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch todos los tickets (sin separar por archivados)
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        // Obtener todos los tickets (abiertos, retenidos y cerrados)
        const respNormales = await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`);
        const respArchivados = await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`);

        const ticketsNormales = respNormales.data.tickets || [];
        const ticketsArchivados = respArchivados.data.tickets || [];

        // Combinar todos los tickets
        const todosTickets = [...ticketsNormales, ...ticketsArchivados];

        // Ordenar por fecha de creación (más recientes primero)
        const sorted = todosTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setTodosLosTickets(sorted);
      } catch (err) {
        console.error('Error al obtener tickets:', err);
      } finally {
        setDatosCargados(true);
      }
    };

    fetchTickets();
  }, []);

  // Fetch técnicos para el filtro
  useEffect(() => {
    const fetchTecnicos = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/usuario/tecnicos`);
        setTecnicos(resp.data.usuarios);
      } catch (err) {
        console.error('Error al obtener técnicos:', err);
      }
    };
    fetchTecnicos();
  }, []);

  // Filtrado de tickets
  useEffect(() => {
    const filtered = todosLosTickets.filter(ticket => {
      // Filtro de estado del ticket
      const matchesEstadoTicket = selectedEstadoTicket === '' ||
        ticket.estado_del_ticket.toLowerCase() === selectedEstadoTicket.toLowerCase();

      // Búsqueda por identificador, asunto o creador
      const matchesSearch =
        ticket.asunto.toLowerCase().includes(searchText.toLowerCase()) ||
        ticket.identificador.toString().includes(searchText) ||
        `${ticket.nombre} ${ticket.apellidos}`.toLowerCase().includes(searchText.toLowerCase());

      // Filtro de departamento
      const matchesDept = selectedDept
        ? ticket.dpto.toLowerCase() === selectedDept.toLowerCase()
        : (tokenInfo.dpto_tecnico
          ? ticket.dpto.toLowerCase() === tokenInfo.dpto_tecnico.toLowerCase()
          : true);

      // Filtro de estado del chat
      const matchesChat =
        selectedEstadoChat === '' ||
        ticket.estado_del_chat.toLowerCase() === selectedEstadoChat.toLowerCase();

      // Filtro de prioridad
      const matchesPrio =
        selectedPrioridad === '' ||
        ticket.prioridad.toLowerCase() === selectedPrioridad.toLowerCase();

      // Filtro de técnico asignado
      const matchesUser =
        selectedUsuarioEncargado === '' ||
        ticket.usuario_encargado.toLowerCase().includes(selectedUsuarioEncargado.toLowerCase());

      return matchesEstadoTicket && matchesSearch && matchesDept && matchesChat && matchesPrio && matchesUser;
    });

    setFilteredTickets(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambian los filtros
  }, [
    todosLosTickets,
    selectedEstadoTicket,
    searchText,
    selectedDept,
    selectedEstadoChat,
    selectedPrioridad,
    selectedUsuarioEncargado,
    tokenInfo.dpto_tecnico
  ]);

  // Paginación
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const startIdx = (currentPage - 1) * ticketsPerPage;
  const currentTickets = filteredTickets.slice(startIdx, startIdx + ticketsPerPage);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setSelectedEstadoTicket('');
    setSelectedDept('');
    setSelectedEstadoChat('');
    setSelectedPrioridad('');
    setSelectedUsuarioEncargado('');
    setSearchText('');
  };

  if (!datosCargados || !minimoTiempoCumplido) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  if (tokenInfo.estado === "verificar") {
    return <Verificacion />;
  }

  return (
    <main className='site-width'>
      <div className="dashboard-content gap-tickets">
        <h2>Panel de tickets</h2>
        <p>Gestiona y supervisa todos los tickets del sistema.</p>

        <div className="layout-tickets">
          {/* Menú lateral de filtros */}
          <aside
            ref={menuRef}
            className={`menu-lateral-filtros ${mostrarFiltros ? 'visible' : 'oculto'}`}
          >
            <h3>Filtrado de tickets</h3>

            <div className="grupos-de-filtrado">

              <div className="grupo-filtro">
                <label>Estado del ticket</label>
                <select
                  value={selectedEstadoTicket}
                  onChange={(e) => setSelectedEstadoTicket(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="abierto">Abierto</option>
                  <option value="retenido">Retenido</option>
                  <option value="cerrado">Cerrado</option>
                </select>
              </div>

              <div className="grupo-filtro">
                <label>Departamento</label>
                <select
                  value={tokenInfo.dpto_tecnico || selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  disabled={!!tokenInfo.dpto_tecnico}
                >
                  <option value="">Todos</option>
                  <option value="no_especificado">Sin Especificar</option>
                  <option value="diseno">Diseño</option>
                  <option value="marketing">Marketing</option>
                  <option value="desarrollo">Desarrollo</option>
                  <option value="redes_sociales">Redes Sociales</option>
                </select>
              </div>

              <div className="grupo-filtro">
                <label>Estado del chat</label>
                <select
                  value={selectedEstadoChat}
                  onChange={(e) => setSelectedEstadoChat(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="sin_responder">Sin Responder</option>
                  <option value="respondido">Respondido</option>
                </select>
              </div>

              <div className="grupo-filtro">
                <label>Prioridad</label>
                <select
                  value={selectedPrioridad}
                  onChange={(e) => setSelectedPrioridad(e.target.value)}
                >
                  <option value="">Todas</option>
                  <option value="no_urgente">No urgente</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div className="grupo-filtro">
                <label>Técnico asignado</label>
                <select
                  value={selectedUsuarioEncargado}
                  onChange={(e) => setSelectedUsuarioEncargado(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="sin_asignar">Sin Asignar</option>
                  {tecnicos.map(t => (
                    <option key={t.id} value={t.nombre}>
                      {t.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button className="boton-limpiar-filtros" onClick={limpiarFiltros}>
              Resetear filtro
            </button>
          </aside>

          {/* Contenido principal */}
          <div className="tabcontent">
            <div className='grid-historial'>
              {/* Barra de búsqueda */}
              <div className="filtro-tickets-admin">
                <button
                  className="boton-toggle-filtros"
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                >
                  + Filtros
                </button>
                <input
                  type="text"
                  placeholder="Buscar por identificador, asunto o creador del ticket..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </div>

              {/* Información de resultados */}
              {/* The user explicitly asked to remove this section */}
              {/* <div className="info-resultados">
                <p>
                  Mostrando {currentTickets.length} de {filteredTickets.length} tickets
                  {mostrarArchivados ? ' archivados' : ' activos'}
                </p>
              </div> */}

              {/* Tabla de tickets */}
              <div className="overflow-tabla">
                <div className='panel-tickets'>
                  <div className="ticket-list-header">
                    <p>ID</p>
                    <p>Creador</p>
                    <p>Asunto</p>
                    <p>Estado</p>
                    <p>Dpto</p>
                    <p>Chat</p>
                    <p>Prioridad</p>
                    <p>Fecha</p>
                    <p>Técnico</p>
                  </div>

                  <div className="tickets-list">
                    {currentTickets.length === 0 ? (
                      <div className="no-tickets">No se han encontrado tickets</div>
                    ) : (
                      currentTickets.map((ticket) => (
                        <Link
                          key={ticket.identificador}
                          className='link-ticket'
                          to={`/ticket/${ticket.identificador}`}
                        >
                          <div className="ticket-item">
                            <div className="ticket-field id">
                              #{ticket.identificador}
                            </div>
                            <div className="ticket-field user">
                              {ticket.nombre} {ticket.apellidos}
                            </div>
                            <div className="ticket-field asunto">
                              {ticket.asunto.split(' ').slice(0, 5).join(' ')}
                              {ticket.asunto.split(' ').length > 5 ? '...' : ''}
                            </div>
                            <div className="ticket-field estado">
                              {ticket.estado_del_ticket === 'abierto' && <p className='abierto'>Abierto</p>}
                              {ticket.estado_del_ticket === 'cerrado' && <p className='cerrado'>Cerrado</p>}
                              {ticket.estado_del_ticket === 'retenido' && <p className='retenido'>Retenido</p>}
                            </div>
                            <div className="ticket-field dpto">
                              {ticket.dpto === 'no_especificado' && <p className='no-especificado'>Sin especificar</p>}
                              {ticket.dpto === 'desarrollo' && <p className='desarrollo'>Desarrollo</p>}
                              {ticket.dpto === 'diseno' && <p className='diseno'>Diseño</p>}
                              {ticket.dpto === 'marketing' && <p className='marketing'>Marketing</p>}
                              {ticket.dpto === 'redes_sociales' && <p className='redes-sociales'>Redes Sociales</p>}
                            </div>
                            <div className="ticket-field chat-estado">
                              {ticket.estado_del_chat === 'sin_responder' && <p className='sin-responder'>Sin Responder</p>}
                              {ticket.estado_del_chat === 'respondido' && <p className='respondido'>Respondido</p>}
                            </div>
                            <div className="ticket-field prioridad">
                              {ticket.prioridad === 'no_urgente' && <p className='sin-prioridad'>No Urgente</p>}
                              {ticket.prioridad === 'urgente' && <p className='alta'>Urgente</p>}
                            </div>
                            <div className="ticket-field fecha">
                              {new Date(ticket.createdAt).toLocaleDateString()}
                            </div>
                            <div className="ticket-field asignado">
                              {ticket.usuario_encargado === 'sin_asignar' ? (
                                <div className='asignado'>
                                  <img src="/images/sin_asignar.svg" alt="Sin asignar" />
                                  <p>Sin asignar</p>
                                </div>
                              ) : (
                                <div className='asignado'>
                                  <img src="/images/asignado.svg" alt={ticket.usuario_encargado} />
                                  <p>@{ticket.usuario_encargado}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Paginación */}
              {filteredTickets.length > 0 && totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                  {/* The user explicitly asked to remove this span */}
                  {/* <span className="info-pagina">
                    Página {currentPage} de {totalPages}
                  </span> */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TicketManager;
